import {
  bomPrintDescTdStyle,
  bomPrintTdStyle,
  bomPrintThStyle,
  BOM_PRINT_ACCENT,
  BOM_PRINT_DESC,
  BOM_PRINT_FONT,
  BOM_PRINT_LABEL,
  BOM_PRINT_ROW_BORDER,
  BOM_PRINT_TEXT,
} from "@/lib/bomPrintDesign";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import {
  blankRepeatedScheduleRooms,
  type DtoolsPdfScheduleLine,
} from "@shared/dtoolsPdfQuotationSchedule";

type Props = {
  lines: DtoolsPdfScheduleLine[];
  /** When true, show Unit Price and Line Total columns. */
  showItemPricing: boolean;
  testId?: string;
};

/**
 * Quotation equipment schedule using BOM table typography/spacing.
 * Column order (pricing visible): Room | Brand | Model | Description | Unit Price | Line Total | Qty
 * Supporting components nest under the parent description (existing accessories grouping).
 * Room labels print only when the room changes.
 */
export default function DtoolsPdfEquipmentScheduleTable({
  lines,
  showItemPricing,
  testId = "dtools-pdf-equipment-schedule",
}: Props) {
  if (lines.length === 0) {
    return (
      <p className="text-[10px] text-[#6B7280]" data-testid={testId} style={{ fontFamily: BOM_PRINT_FONT }}>
        No equipment lines in this section.
      </p>
    );
  }

  const displayLines = blankRepeatedScheduleRooms(lines);

  return (
    <div data-testid={testId} data-pdf-schedule-table="true" style={{ fontFamily: BOM_PRINT_FONT }}>
      <h3
        className="mb-1 font-semibold uppercase tracking-[0.05em]"
        data-pdf-schedule-heading="true"
        style={{ fontSize: 9, letterSpacing: "0.05em", color: BOM_PRINT_ACCENT }}
      >
        Equipment Schedule
      </h3>
      <table
        className="dtools-pdf-schedule-table w-full border-collapse"
        style={{ width: "100%", tableLayout: "fixed", fontSize: 10, borderCollapse: "collapse" }}
      >
        <colgroup>
          <col style={{ width: showItemPricing ? "12%" : "16%" }} />
          <col style={{ width: showItemPricing ? "11%" : "14%" }} />
          <col style={{ width: showItemPricing ? "16%" : "22%" }} />
          <col style={{ width: showItemPricing ? "28%" : "42%" }} />
          {showItemPricing ? (
            <>
              <col style={{ width: "12%" }} />
              <col style={{ width: "13%" }} />
            </>
          ) : null}
          <col style={{ width: showItemPricing ? "8%" : "6%" }} />
        </colgroup>
        <thead>
          <tr>
            <th style={bomPrintThStyle}>Room</th>
            <th style={bomPrintThStyle}>Brand</th>
            <th style={bomPrintThStyle}>Model</th>
            <th style={bomPrintThStyle}>Description</th>
            {showItemPricing ? (
              <>
                <th style={{ ...bomPrintThStyle, textAlign: "right" }}>Unit Price</th>
                <th style={{ ...bomPrintThStyle, textAlign: "right" }}>Line Total</th>
              </>
            ) : null}
            <th style={{ ...bomPrintThStyle, textAlign: "right" }}>Qty</th>
          </tr>
        </thead>
        <tbody data-pdf-schedule-body="true">
          {displayLines.map((line, index) => (
            <tr
              key={line.id || `${line.model}-${index}`}
              data-testid={`dtools-pdf-schedule-row-${line.id || index}`}
              data-pdf-has-supporting={(line.supportingComponents?.length ?? 0) > 0 ? "true" : undefined}
              style={{ borderBottom: `1px solid ${BOM_PRINT_ROW_BORDER}` }}
            >
              <td style={{ ...bomPrintTdStyle, wordBreak: "break-word", color: BOM_PRINT_LABEL }}>
                {line.roomDisplay}
              </td>
              <td
                style={{
                  ...bomPrintTdStyle,
                  wordBreak: "break-word",
                  fontWeight: 600,
                  color: BOM_PRINT_TEXT,
                }}
              >
                {line.brand}
              </td>
              <td
                style={{
                  ...bomPrintTdStyle,
                  wordBreak: "break-word",
                  fontWeight: 500,
                  color: BOM_PRINT_TEXT,
                }}
              >
                {line.model}
              </td>
              <td style={{ ...bomPrintDescTdStyle, color: BOM_PRINT_DESC }}>
                {line.description ? (
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 400,
                      lineHeight: 1.45,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {line.description}
                  </p>
                ) : (
                  <p style={{ margin: 0 }}>—</p>
                )}
                {(line.supportingComponents?.length ?? 0) > 0 ? (
                  <div
                    data-testid={`dtools-pdf-supporting-${line.id || index}`}
                    style={{ marginTop: 6 }}
                  >
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: 14,
                        fontSize: 8,
                        lineHeight: 1.4,
                        color: BOM_PRINT_LABEL,
                        fontWeight: 400,
                      }}
                    >
                      {line.supportingComponents!.map((item) => (
                        <li key={item.id} style={{ marginBottom: 1 }}>
                          {item.label}
                          {item.qtyLabel && item.qtyLabel !== "1" ? ` × ${item.qtyLabel}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </td>
              {showItemPricing ? (
                <>
                  <td
                    style={{
                      ...bomPrintTdStyle,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatDtoolsMoney(line.unitPrice)}
                  </td>
                  <td
                    style={{
                      ...bomPrintTdStyle,
                      textAlign: "right",
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                      color: BOM_PRINT_TEXT,
                    }}
                  >
                    {formatDtoolsMoney(line.lineTotal)}
                  </td>
                </>
              ) : null}
              <td
                style={{
                  ...bomPrintTdStyle,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 500,
                }}
              >
                {line.qtyLabel}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
